import { useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { identity } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import Sentry from "@similarweb/sentry-logger";
import { BasicModal } from "@similarweb/ui-components";

import type { ToastAction } from "app/actions/toast_actions";
import { useDidUpdate } from "app/custom-hooks/useDidUpdate";
import { translate } from "app/filters/i18nFilter";
import useSetTimeout from "app/pages/sales-intelligence/hooks/useSetTimeout";
import { ContactsModalContent } from "app/pages/sales-intelligence/modules/enrichment/components/ModalContent/ContactsModalContent";
import type { ContactsEnrichmentModalStage } from "app/pages/sales-intelligence/modules/enrichment/components/type";
import { ENRICHING_ERROR } from "app/pages/sales-intelligence/modules/enrichment/constants/constants";
import { EUploadCSVEnrichmentStage } from "app/pages/sales-intelligence/modules/enrichment/constants/enums";
import { useToasts } from "app/pages/sales-intelligence/modules/enrichment/hooks/useToasts";
import { enrichmentQueryKeys } from "app/pages/sales-intelligence/modules/enrichment/queries/key-factory/queryKeyFactory";
import useEnrichmentList from "app/pages/sales-intelligence/modules/enrichment/queries/useEnrichmentList";
import {
    clearEnrichmentParsing,
    enrichmentFinishedAction,
} from "app/pages/sales-intelligence/modules/enrichment/store/actions/enrichment";
import {
    getEnrichedContactsCsvThunk,
    parseContactsEnrichmentCsvThunk,
    postContactsEnrichmentCsvThunk,
} from "app/pages/sales-intelligence/modules/enrichment/store/effects/contactsEnrichment";
import type { PostContactsCsvProps } from "app/pages/sales-intelligence/modules/enrichment/store/type";
import { EnrichmentStatus } from "app/pages/sales-intelligence/modules/enrichment/store/type";
import {
    ERROR_TOAST,
    LOADING_TOAST,
    SUCCESS_TOAST,
} from "app/pages/sales-intelligence/modules/enrichment/utils/i18n/download.texts";
import { mapContactEnrichmentToFormData } from "app/pages/sales-intelligence/modules/enrichment/utils/utils";
import { quotaQueryClient } from "app/pages/sales-intelligence/modules/quota/queries/client";
import enrichmentTrackingService from "app/pages/sales-intelligence/services/tracking/enrichment/trackingService";
import { selectRemainingQuota } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import type {
    ContactsHeaderSelection,
    ParsedContactsResult,
} from "app/pages/sales-intelligence/sub-modules/management/components/types";
import { WithKeysInput } from "app/pages/sales-intelligence/sub-modules/management/hocs/withKeysInput";
import { downloadFile } from "app/pages/sales-intelligence/utils/downloadFile";
import type { IProModalCustomStyles } from "pro-features/components/Modals/src/ProModal";

import type { UploadEnrichmentListModalProps } from "./type";

const CUSTOM_MODAL_STYLES: IProModalCustomStyles = {
    content: { padding: 0, width: "auto", transition: "0.5s ease-out" },
};

const CLOSE_ICON_CONFIGS = { show: false };
const REFRESH_TIMEOUT = 5000;

export const UploadContactsEnrichmentModal = ({
    isOpen,
    onOpenStateChange,
    file,
    setFile,
    onFileChange,
}: UploadEnrichmentListModalProps) => {
    const dispatch = useDispatch();
    const [modalStage, setModalStage] = useState<ContactsEnrichmentModalStage>(EUploadCSVEnrichmentStage.Upload);

    const loadingToastRef = useRef<ToastAction>(null);
    const [selectedHeaders, setSelectedHeaders] = useState<ContactsHeaderSelection>({});

    const [csvId, setCsvId] = useState<string | null>(null);
    const [enrichmentProps, setEnrichmentProps] = useState<PostContactsCsvProps>();

    const queryClient = useQueryClient();
    const { refetch } = useEnrichmentList({ enabled: false });

    const contactsRemainingQuota = useSelector(selectRemainingQuota);

    const localSetTimeout = useSetTimeout();

    const onClose = () => {
        onOpenStateChange(false);
        setFile(null);
    };

    const fileName = file?.name || "";

    const { showErrorToast, showSuccessToast, showLoadingToast, hideToast } = useToasts();

    const handleLoadingFinished = () => {
        if (loadingToastRef.current?.toast?.timestamp) {
            hideToast(loadingToastRef.current?.toast);
        }
        loadingToastRef.current = null;
        dispatch(enrichmentFinishedAction());
    };

    const onConfirmAsync = async () => {
        if (file && enrichmentProps) {
            enrichmentTrackingService.csv.contacts.trackApproveEnrichment(
                enrichmentProps.parsingResult.totalRowCount,
                enrichmentProps.allSelectedColumnsLength,
            );

            loadingToastRef.current = showLoadingToast(translate(LOADING_TOAST));
            const postedCsvId = await dispatch(
                postContactsEnrichmentCsvThunk(
                    mapContactEnrichmentToFormData(enrichmentProps, selectedHeaders, fileName),
                ),
            );

            void queryClient.invalidateQueries({ queryKey: enrichmentQueryKeys.lists() });
            void quotaQueryClient.invalidateDataCreditsState();

            if (postedCsvId === null) {
                handleLoadingFinished();
                showErrorToast(translate(ERROR_TOAST));
                return;
            }

            setCsvId(postedCsvId);
        }
    };

    useEffect(() => {
        if (enrichmentProps) {
            onClose();
            onConfirmAsync().catch((error) => {
                Sentry.error("Error uploading CSV for contact enrichment", error);
            });
        }
    }, [enrichmentProps]);

    const onConfirm = (props: PostContactsCsvProps) => {
        setEnrichmentProps(props);
    };

    const refreshCsvStatus = async () => {
        const { data } = await refetch();
        const currentCsvEnrichment = data?.find((csvStatus) => csvStatus.id === csvId);

        if (currentCsvEnrichment?.status === EnrichmentStatus.InProgress) {
            localSetTimeout(() => {
                refreshCsvStatus();
            }, REFRESH_TIMEOUT);
        }
        if (currentCsvEnrichment?.status === EnrichmentStatus.Succeeded) {
            handleLoadingFinished();
            const result = await dispatch(getEnrichedContactsCsvThunk(JSON.stringify(csvId)));

            enrichmentTrackingService.csv.contacts.trackSuccessEnrichment(
                enrichmentProps?.parsingResult?.totalRowCount ?? 0,
                currentCsvEnrichment.enrichedContactsCount ?? 0,
            );
            showSuccessToast(translate(SUCCESS_TOAST));
            downloadFile(file?.name ?? "", result);

            void queryClient.invalidateQueries({ queryKey: enrichmentQueryKeys.lists() });
            void quotaQueryClient.invalidateDataCreditsState();
        }
        if (currentCsvEnrichment?.status === EnrichmentStatus.Failed) {
            handleLoadingFinished();
            showErrorToast(translate(ERROR_TOAST));
            enrichmentTrackingService.csv.contacts.trackFailureEnrichment(
                enrichmentProps?.parsingResult?.totalRowCount ?? 0,
            );
            Sentry.error(
                "error in refreshCsvStatus in UploadContactsEnrichmentListModal.tsx",
                new Error(ENRICHING_ERROR),
            );
        }
    };

    useDidUpdate(() => {
        if (csvId) {
            localSetTimeout(() => {
                void refreshCsvStatus();
            }, REFRESH_TIMEOUT);
        }
    }, [csvId]);

    const parseFile = async () => {
        const result = await dispatch(parseContactsEnrichmentCsvThunk(file!, contactsRemainingQuota));
        return result as ParsedContactsResult;
    };

    useEffect(() => {
        if (file) {
            parseFile().then((result) => {
                if (result && result.totalRowCount) {
                    enrichmentTrackingService.csv.contacts.trackFileParse(result.totalRowCount);

                    setModalStage(EUploadCSVEnrichmentStage.Enrichment);
                    setSelectedHeaders({
                        linkedinUrl: result.linkedinHeaderNames[0],
                        domain: result.companyHeaderNames[0],
                        fullName: result.fullnameHeaderNames[0],
                    });
                }
            });
        }
    }, [file]);

    useEffect(() => {
        if (isOpen) {
            dispatch(clearEnrichmentParsing());
            setModalStage(EUploadCSVEnrichmentStage.Upload);
        }
    }, [isOpen]);

    const onReplace = () => {
        setModalStage(EUploadCSVEnrichmentStage.Upload);
        setFile(null);
    };

    return (
        <BasicModal
            data-automation="enrichment-upload-csv-modal"
            customStyles={CUSTOM_MODAL_STYLES}
            isOpen={isOpen}
            closeIconConfig={CLOSE_ICON_CONFIGS}
            shouldCloseOnOverlayClick
        >
            <WithKeysInput
                onEnter={identity}
                onEscape={() => onClose()}
            >
                <ContactsModalContent
                    setSelectedHeaders={setSelectedHeaders}
                    onClose={onClose}
                    onConfirm={onConfirm}
                    onFileChange={onFileChange}
                    onReplace={onReplace}
                    selectedHeaders={selectedHeaders}
                    fileName={fileName}
                    modalStage={modalStage}
                />
            </WithKeysInput>
        </BasicModal>
    );
};
